import { STATUS } from "@/constants/statuses";
import { transformMeta } from "@apps/iloom_customization/helpers/transformMeta";
import { ProductService } from "@apps/iloom_customization/modules/products/services/ProductService";
import { iloomCustomizationApplicationLoggerToken } from "@apps/iloom_customization/tokens";
import WishlistedProductsStore from "@apps/iloom_customization/wishlist/stores/WishlistedProductsStore";
import { WishlistsApiService } from "@apps/wishlist/api_services/WishlistsApiService/WishlistsApiService";
import { WishlistItemEntity } from "@apps/wishlist/entities/WishlistItemEntity";
import WishlistsStore from "@apps/wishlist/stores/WishlistsStore";
import { QueryPaginationMeta } from "@apps/wishlist/types";
import { applyTransaction, arrayRemove } from "@datorama/akita";
import { omit } from "rambda";
import { of, switchMap, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

@singleton()
export class WishlistService {
    constructor(
        private readonly productService: ProductService,
        private readonly wishlistedProductsStore: WishlistedProductsStore,
        private readonly wishlistsApiService: WishlistsApiService,
        private readonly wishlistsStore: WishlistsStore,

        @inject(iloomCustomizationApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    getWishlistItems(queryParams: QueryPaginationMeta, isLoadMore = false) {
        this.wishlistedProductsStore.update((state) => {
            if (isLoadMore && queryParams.offset) {
                state.loadMoreStatus = STATUS.LOADING;
            } else {
                state.meta.offset = queryParams.offset;
                state.status = STATUS.LOADING;
            }
        });

        return of(null).pipe(
            switchMap(() => {
                return this.wishlistsApiService.getWishlistItems(queryParams);
            }),
            tap((value) => {
                value.items.forEach((item) => {
                    this.productService.addProductForFetching(item.productId);
                });
            }),
            tap((value) => {
                applyTransaction(() => {
                    this.wishlistedProductsStore.update((state) => {
                        const meta = transformMeta(omit(["items"], value));
                        const items =
                            isLoadMore && queryParams.offset
                                ? [...state.wishlistedProducts, ...value.items]
                                : value.items;

                        state.wishlistedProducts = items;
                        state.meta = meta;
                        state.loadMoreStatus = STATUS.READY;
                        state.status = STATUS.READY;
                    });
                });
            })
        );
    }

    deleteWishlistItem(payload: {
        wishlistId: number | null;
        productId: number;
        variantId: number | null;
    }) {
        this.logger.debug("WishlistsService.deleteItem", payload);
        return this.wishlistsApiService.deleteItem(payload).pipe(
            tap((value) => {
                applyTransaction(() => {
                    this.wishlistedProductsStore.update((state) => {
                        state.wishlistedProducts = arrayRemove(
                            state.wishlistedProducts,
                            (item: WishlistItemEntity) =>
                                item.productId === value.productId &&
                                item.variantId === value.variantId
                        );
                    });
                });
            })
        );
    }

    getWishlists() {
        return of(null).pipe(
            switchMap(() => {
                return this.wishlistsApiService.getWishlists();
            }),
            tap((v) => {
                applyTransaction(() => {
                    this.wishlistsStore.add(v);
                    this.wishlistsStore.setStatus(STATUS.READY);
                });
            })
        );
    }
}
